import type { TFunction } from "i18next"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import LoopIcon from "@mui/icons-material/Loop"
import fp from "lodash/fp"
import type { GridColDef } from "@mui/x-data-grid"
import WarningIcon from "@mui/icons-material/WarningRounded"
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff"
import AdjustIcon from "@mui/icons-material/Adjust"
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline"

import type {
  AlertSettingParam,
  AlertSettingStatesById,
  AlertSettingState,
  AlertSettingType,
  AlertSettingUpsertForm,
  AlertsRes,
  AlertsTableRow,
  AlertState,
  EventDescription,
  ThresholdsField,
  AlertSetting,
  AlertSettingParamSchema,
  AlertSettingConfigThreshold,
  AlertSettingConfig,
  Alert,
  AlertSettingAccordion,
  PowerlineAlertSetting,
  TowerAlertSetting,
  AlertSettingWState,
  DeviceAlertSetting,
  DeviceAlertSettingWState,
  AlertsSummaryEntity,
  OpenAlertsSummary,
} from "types/alerts.types"
import type { TelemetryOptionType } from "types/telemetries.types"
import type { InputRules, EntityType } from "types/common.types"
import type { Powerline } from "types/powerlines.types"
import type { Tower } from "types/towers.types"
import type { IDeviceWRelsById } from "types/device.types"
import type { IGroupsWRelsById } from "types/group.types"
import type {
  LocaleNumberToStandardFn,
  NumberToLocaleFn,
} from "helpers/utils/localeNumber"

import { alertTypeDefs } from "helpers/config/dashboards/global/alerts"
import { formatDateFromMillis } from "helpers/utils/dateUtils"
import { isVirtualDevice } from "helpers/utils/devices"
import { numberToLocale, localeNumberToStandard } from "helpers/utils/localeNumber"
import { nameOrUnnamed } from "helpers/utils/translations"
import IconTextStack from "widgets/common/IconTextStack"
import EntityCell from "widgets/common/EntityCell"

export const isTriggeredState = (state: AlertSettingState | undefined): boolean => {
  const triggered = fp.getOr(false, "triggered", state)
  const silenced = fp.getOr(false, "silenced", state)
  return triggered && !silenced
}

export const hasOrgTriggeredAlerts = (
  orgActiveDevicesAlertSettings: AlertSetting[] | undefined,
  statesBySettingId: { [key: string]: AlertSettingState },
): boolean => {
  return fp.flow(
    fp.defaultTo([] as AlertSetting[]),
    fp.filter((setting: AlertSetting) => isTriggeredState(statesBySettingId[setting.id])),
    fp.negate(fp.isEmpty),
  )(orgActiveDevicesAlertSettings)
}

export const entityHasTriggeredAlerts = (
  settingsStates: AlertSettingWState[] | undefined,
  activeSettingsById: { [key: string]: AlertSetting },
): boolean =>
  fp.flow(
    fp.defaultTo([] as AlertSettingWState[]),
    fp.some(
      (settingState: AlertSettingWState) =>
        Boolean(activeSettingsById[settingState.id]) &&
        isTriggeredState(settingState.state),
    ),
  )(settingsStates)

// The group alerts tab lists the alerts of the devices in the group
export const groupHasTriggeredAlerts = (
  deviceSettingsStates: DeviceAlertSettingWState[] | undefined,
  activeDeviceSettingsById: { [key: string]: DeviceAlertSetting },
  devsWRelsById: IDeviceWRelsById | undefined,
  groupId: number,
): boolean =>
  fp.flow(
    fp.defaultTo([] as DeviceAlertSettingWState[]),
    fp.filter((settingState: DeviceAlertSettingWState) =>
      fp.some(
        { id: groupId },
        fp.getOr([], [settingState.entity_id, "groups"], devsWRelsById),
      ),
    ),
    (groupSettingsStates: DeviceAlertSettingWState[]) =>
      entityHasTriggeredAlerts(groupSettingsStates, activeDeviceSettingsById),
  )(deviceSettingsStates)

export const ALERTS_SUMMARY_ENTITIES: AlertsSummaryEntity[] = [
  "powerline",
  "tower",
  "device",
]

// Counts the same open alerts the Alerts page lists, so the two numbers agree
export const getOpenAlertsSummary = (
  settingsStates: AlertSettingWState[] | undefined,
  activeSettingsById: { [key: string]: AlertSetting },
  devsWRelsById: IDeviceWRelsById | undefined,
): OpenAlertsSummary => {
  const openAlerts = (settingsStates ?? []).filter(
    (settingState) =>
      settingState.entity !== "group" &&
      Boolean(activeSettingsById[settingState.id]) &&
      isTriggeredState(settingState.state) &&
      (settingState.entity !== "device" || fp.has(settingState.entity_id, devsWRelsById)),
  )
  const affectedByEntity = fp.fromPairs(
    ALERTS_SUMMARY_ENTITIES.map((entity) => [
      entity,
      fp.flow(fp.filter({ entity }), fp.map("entity_id"), fp.uniq, fp.size)(openAlerts),
    ]),
  ) as Record<AlertsSummaryEntity, number>

  return {
    openAlerts: openAlerts.length,
    affectedEntities: fp.sum(fp.values(affectedByEntity)),
    affectedByEntity,
  }
}

export const hasTriggeredAlerts = (states: AlertSettingStatesById) =>
  states && Object.values(states).some((state) => isTriggeredState(state))

export const hasDeviceTriggeredAlerts = (
  states: AlertSettingStatesById,
  orgAlertsBySettingId: { [key: string]: AlertSetting },
) => {
  return fp.flow(
    fp.toPairs,
    fp.some(
      ([settingId, settingState]: [string, AlertSettingState]) =>
        isTriggeredState(settingState) &&
        Boolean(orgAlertsBySettingId[settingId]?.config?.active),
    ),
  )(states)
}

export const getAlertSettingParamRules = (param: AlertSettingParam, t: TFunction) => {
  const message = t("generic.FIELD_REQUIRED") || ""
  let result: InputRules = { required: param?.required ? message : false }
  if (!param.schema || !("type" in param.schema) || param.schema.type !== "number") {
    return result
  }
  const schema = param.schema
  if (schema.minimum === 0) {
    result = {
      ...result,
      min: {
        value: schema.minimum,
        message: t("generic.FIELD_MIN_VALUE", { minValue: schema.minimum }),
      },
    }
  }

  return result
}

export const formatThresholds = (thresholds: ThresholdsField[], locale = "en") => {
  const parseValue: LocaleNumberToStandardFn = (value) =>
    localeNumberToStandard(value, locale)
  const result = thresholds
    .map((threshold) => {
      switch (threshold.rangeCondition) {
        case "above":
          return {
            min: parseValue(threshold[threshold.rangeCondition]),
            target: threshold.alertStatus.toLowerCase(),
          }
        case "below":
          return {
            max: parseValue(threshold[threshold.rangeCondition]),
            target: threshold.alertStatus.toLowerCase(),
          }
        case "inside":
          return {
            min: parseValue(threshold[threshold.rangeCondition].from),
            max: parseValue(threshold[threshold.rangeCondition].to),
            target: threshold.alertStatus.toLowerCase(),
          }
        case "outside":
          return {
            max: parseValue(threshold[threshold.rangeCondition].from),
            min: parseValue(threshold[threshold.rangeCondition].to),
            target: threshold.alertStatus.toLowerCase(),
          }
        default:
          return null
      }
    })
    .filter((threshold) => threshold !== null && threshold !== undefined)
  return result
}

export const reverseFormatThresholds = (
  thresholds: AlertSettingConfigThreshold[],
  locale = "en",
) => {
  const formatValue: NumberToLocaleFn = (v) => numberToLocale(v, locale)

  const result = thresholds.map((threshold) => {
    const above = threshold.max === undefined && threshold.min !== undefined
    const below = threshold.max !== undefined && threshold.min === undefined
    const inside =
      threshold.min !== undefined &&
      threshold.max !== undefined &&
      threshold.min < threshold.max

    if (above) {
      return {
        rangeCondition: "above",
        above: formatValue(threshold.min!),
        alertStatus: threshold.target.toUpperCase(),
      }
    } else if (below) {
      return {
        rangeCondition: "below",
        below: formatValue(threshold.max!),
        alertStatus: threshold.target.toUpperCase(),
      }
    } else if (inside) {
      return {
        rangeCondition: "inside",
        inside: {
          from: formatValue(threshold.min!),
          to: formatValue(threshold.max!),
        },
        alertStatus: threshold.target.toUpperCase(),
      }
    } else {
      return {
        rangeCondition: "outside",
        outside: {
          from: formatValue(threshold.max!),
          to: formatValue(threshold.min!),
        },
        alertStatus: threshold.target.toUpperCase(),
      }
    }
  })
  return result
}

export const formatMutationParams = (
  data: AlertSettingUpsertForm,
  settingType?: AlertSettingType,
  locale = "en",
) => {
  const { type, thresholds, fields, ...rest } = data

  // Aux functions
  const formatSchemasById = fp.flow(
    fp.map(({ id, schema, required }) =>
      schema ? [id, { schema, required }] : [id, { required }],
    ),
    fp.fromPairs,
  )
  const tweakNumbers = (schemasById: {
    [key: string]: { schema?: AlertSettingParamSchema; required: boolean | undefined }
  }) =>
    fp.flow(
      fp.toPairs,
      fp.map(([key, value]) => {
        const { schema, required } = schemasById[key]
        if (!required && value === "") {
          return [key, null]
        }
        if (schema && "type" in schema && schema.type === "number") {
          return [key, localeNumberToStandard(value, locale)]
        }
        return [key, value]
      }),
      fp.fromPairs,
    )

  const typedThresholds = thresholds as ThresholdsField[]
  const typedTelemetries = fields as TelemetryOptionType[]
  const formattedTelemetries =
    typedTelemetries && typedTelemetries.map((telemetry) => telemetry.value)
  const formattedThresholds = typedThresholds && formatThresholds(typedThresholds, locale)

  const schemasById = formatSchemasById(settingType?.params)
  const formattedConfig = tweakNumbers(schemasById)(rest)

  return {
    config: {
      ...formattedConfig,
      ...(formattedThresholds && {
        thresholds: formattedThresholds,
      }),
      ...(formattedTelemetries && {
        fields: formattedTelemetries,
      }),
    } as AlertSettingConfig,
    type,
  }
}

const getEventDescription = (
  triggered: boolean | undefined,
  resolved: boolean | undefined,
): EventDescription => {
  switch (true) {
    case triggered:
      return {
        icon: <ErrorOutlineIcon color="error" fontSize="small" />,
        description: "alerts.EVENT_TRIGGERED",
      }
    case resolved:
      return {
        icon: <CheckCircleOutlineIcon color="success" fontSize="small" />,
        description: "alerts.EVENT_RESOLVED",
      }
    default:
      return {
        icon: <LoopIcon color="warning" fontSize="small" />,
        description: "alerts.EVENT_RENOTIFICATION",
      }
  }
}

export const getEventsTableRows = (alerts: AlertsRes, t: TFunction) => {
  return alerts?.map((alert) => {
    const { icon, description } = getEventDescription(alert.triggered, alert.resolved)
    return {
      id: alert.id,
      eventDescription: {
        icon,
        description: t(description, { alertName: alert.alert_name }),
      },
      timestamp: formatDateFromMillis(alert.timestamp),
    }
  })
}

// Matches a column's value against the search, lowercased and as a whole phrase.
const includesSearch = (text: string, search: string) =>
  text.toLowerCase().includes(String(search).toLowerCase())

// A single device's page already names its entity.
const hasEntityColumns = (
  devicesIds: number[] | undefined,
  groupId: number | undefined,
) => !devicesIds || !!groupId

export const getAlertsTableColumns = (
  baseOrgURL: string,
  devicesIds: number[] | undefined,
  groupId: number | undefined,
  t: TFunction,
): GridColDef<AlertsTableRow>[] => {
  const translateEntityType = (entityType: EntityType) =>
    t(`entities.${entityType.toUpperCase()}`)
  const entityColumns: GridColDef<AlertsTableRow>[] = [
    {
      field: "entity",
      headerName: t("alerts.ENTITY"),
      flex: 1,
      minWidth: 220,
      // What Ctrl+C copies from a focused cell.
      valueFormatter: (value: AlertsTableRow["entity"]) =>
        `${nameOrUnnamed(value.entityName, t)} - ${value.entityId}`,
      sortComparator: (a: AlertsTableRow["entity"], b: AlertsTableRow["entity"]) =>
        (a.entityName ?? "").localeCompare(b.entityName ?? ""),
      getApplyQuickFilterFn: (search) => (value: AlertsTableRow["entity"]) =>
        includesSearch(String(value.entityId), search) ||
        includesSearch(value.entityName ?? "", search) ||
        includesSearch(translateEntityType(value.entityType), search),
      renderCell: ({ value }) => (
        <EntityCell
          entity={value.entityType}
          id={value.entityId}
          name={value.entityName}
          baseOrgURL={baseOrgURL}
        />
      ),
    },
    // Exported, never shown: the entity column shows all three in one cell.
    {
      field: "entityTypeCsv",
      headerName: t("alerts.ENTITY_TYPE"),
      valueGetter: (_value, row) => translateEntityType(row.entity.entityType),
    },
    {
      field: "entityNameCsv",
      headerName: t("generic.NAME"),
      valueGetter: (_value, row) => nameOrUnnamed(row.entity.entityName, t),
    },
    {
      field: "entityIdCsv",
      headerName: t("generic.ID"),
      valueGetter: (_value, row) => row.entity.entityId,
    },
  ]

  return [
    ...(hasEntityColumns(devicesIds, groupId) ? entityColumns : []),
    {
      field: "eventDescription",
      headerName: t("alerts.EVENT_DESCRIPTION"),
      flex: 1,
      minWidth: 250,
      valueGetter: (value: AlertsTableRow["eventDescription"]) => value.description,
      renderCell: ({ row }) => (
        <IconTextStack
          icon={row.eventDescription.icon}
          text={row.eventDescription.description}
          noWrap
        />
      ),
    },
    {
      field: "timestamp",
      headerName: t("generic.TIMESTAMP"),
      flex: 1,
      minWidth: 150,
    },
  ]
}

// The CSV keeps the type, name and id columns; the entity column itself is not exported.
export const getAlertsTableCsvFields = (
  devicesIds: number[] | undefined,
  groupId: number | undefined,
) => [
  ...(hasEntityColumns(devicesIds, groupId)
    ? ["entityTypeCsv", "entityNameCsv", "entityIdCsv"]
    : []),
  "eventDescription",
  "timestamp",
]

export const getEntityValues = (
  alert: Alert,
  powerline: Powerline | undefined,
  tower: Tower | undefined,
): {
  entityId: number
  entityName: string | null
  entityType: EntityType
} => {
  if (powerline) {
    const entityId = powerline.id
    const entityName = powerline.name
    const entityType = "powerline"
    return { entityId, entityName, entityType }
  } else if (tower) {
    const entityId = tower.id
    const entityName = tower.name
    const entityType = "tower"
    return { entityId, entityName, entityType }
  } else if (alert.less_id) {
    const entityId = alert.less_id
    const entityName = alert.device_name
    const entityType = "device"
    return { entityId, entityName, entityType }
  } else {
    const entityId = alert.group_id as number
    const entityName = alert.group_name as string
    const entityType = "group"
    return { entityId, entityName, entityType }
  }
}

export const getAlertsTableRows = (
  alerts: AlertsRes,
  powerlineIdByLessId: { [key: number]: number },
  powerlinesById: { [key: number]: Powerline },
  towerIdByLessId: { [key: number]: number },
  towersById: { [key: number]: Tower },
  t: TFunction,
): AlertsTableRow[] => {
  return alerts?.map((alert) => {
    const powerline =
      (alert.less_id &&
        powerlineIdByLessId[alert.less_id] &&
        powerlinesById[powerlineIdByLessId[alert.less_id]]) ||
      undefined

    const tower =
      (alert.less_id &&
        towerIdByLessId[alert.less_id] &&
        towersById[towerIdByLessId[alert.less_id]]) ||
      undefined

    const { entityId, entityName, entityType } = getEntityValues(alert, powerline, tower)
    const { icon, description } = getEventDescription(alert.triggered, alert.resolved)
    const timestamp = formatDateFromMillis(alert.timestamp)
    return {
      id: alert.id,
      entity: { entityType, entityId, entityName },
      eventDescription: {
        icon,
        description: t(description, { alertName: alert.alert_name }),
      },
      timestamp,
    }
  })
}

export const getAlertsFileEntityName = ({
  devicesIds,
  groupId,
  groupsWRelsById,
  devsWRelsById,
  powerlineIdByLessId,
  powerlinesById,
  towerIdByLessId,
  towersById,
  t,
}: {
  devicesIds: number[] | undefined
  groupId: number | undefined
  groupsWRelsById: IGroupsWRelsById
  devsWRelsById: IDeviceWRelsById | undefined
  powerlineIdByLessId: { [key: number]: number }
  powerlinesById: { [key: number]: Powerline }
  towerIdByLessId: { [key: number]: number }
  towersById: { [key: number]: Tower }
  t: TFunction
}): string => {
  if (groupId && groupsWRelsById[groupId]) {
    return `${groupsWRelsById[groupId].group.name}_`
  }
  if (devicesIds === undefined) {
    return ""
  }
  const lessId = devicesIds[0]
  const powerline = powerlinesById[powerlineIdByLessId[lessId]]
  if (powerline) {
    return `${nameOrUnnamed(powerline.name, t)}_`
  }
  const tower = towersById[towerIdByLessId[lessId]]
  if (tower) {
    return `${nameOrUnnamed(tower.name, t)}_`
  }
  const device = lessId ? devsWRelsById?.[lessId] : undefined
  if (device && !isVirtualDevice(device.device.device_type)) {
    return `${device.device.name}_`
  }
  return ""
}

export const getAlertState = (alertState: AlertSettingState): AlertState => {
  return alertState.silenced && alertState.triggered
    ? "silenced"
    : alertState.triggered
      ? "open"
      : "notTriggered"
}

export const getPowerlineIdsFromEvents = (
  events: Alert[] | undefined,
  powerlineIdByLessId: { [key: number]: number },
): number[] =>
  fp.flow(
    fp.filter(
      (event: Alert) => !!event.less_id && fp.has(event.less_id, powerlineIdByLessId),
    ),
    fp.map((event) => powerlineIdByLessId[Number(event.less_id)]),
    fp.uniq,
  )(events)

export const getTowerIdsFromEvents = (
  events: Alert[] | undefined,
  towerIdByLessId: { [key: number]: number },
): number[] =>
  fp.flow(
    fp.filter(
      (event: Alert) => !!event.less_id && fp.has(event.less_id, towerIdByLessId),
    ),
    fp.map((event) => towerIdByLessId[Number(event.less_id)]),
    fp.uniq,
  )(events)

export const getAlertStateIcon = (alertState: AlertState): JSX.Element => {
  switch (alertState) {
    case "open":
      return <WarningIcon color="error" fontSize="small" />
    case "silenced":
      return <NotificationsOffIcon color="warning" fontSize="small" />
    case "disabled":
      return <PauseCircleOutlineIcon color="disabled" fontSize="small" />
    case "notTriggered":
    default:
      return <AdjustIcon color="action" fontSize="small" />
  }
}

export const getAlertLinkHref = (
  alert: AlertSettingAccordion,
  baseOrgURL: string,
): string => {
  switch (alert.entity) {
    case "powerline":
      return `${baseOrgURL}/powerlines/${alert.entity_id}`
    case "tower":
      return `${baseOrgURL}/towers/${alert.entity_id}`
    case "group":
      return `${baseOrgURL}/device-groups/${alert.entity_id}`
    case "device":
    default:
      return `${baseOrgURL}/devices/${alert.entity_id}`
  }
}

// A non-threshold alert watches its type's own fields rather than configured ones.
export const getAlertTypeFields = (
  type: string,
  configuredFields?: string[],
): string[] => {
  if (type === "threshold") {
    return configuredFields ?? []
  }
  const plotDef = alertTypeDefs[type as keyof typeof alertTypeDefs]
  return (plotDef?.reqConfig.fields as string[] | undefined) ?? []
}

export const getAlertFields = (alert: AlertSettingAccordion): string[] =>
  getAlertTypeFields(alert.type, alert.config.fields as string[] | undefined)

export const getPowerlineIdsFromSettings = (
  orgAlertSettings: PowerlineAlertSetting[],
): number[] =>
  fp.map((setting: PowerlineAlertSetting) => setting.entity_id)(orgAlertSettings)

export const getTowerIdsFromSettings = (
  orgAlertSettings: TowerAlertSetting[],
): number[] => fp.map((setting: TowerAlertSetting) => setting.entity_id)(orgAlertSettings)
